import { useTranslation } from "react-i18next";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Loading is a first-class state, not a spinner. Skeletons that match the shape
 * of the content stop the layout jumping on a slow 3G connection, which is the
 * common case at a gym front desk.
 */

interface LoadingStateProps {
  /** How many skeleton rows to draw. */
  rows?: number;
  className?: string;
  label?: string;
}

export function LoadingState({ rows = 4, className, label }: LoadingStateProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn("space-y-3", className)}
      role="status"
      aria-busy="true"
      aria-label={label ?? t("a11y.loading")}
    >
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className="h-12 w-full rounded-lg" />
      ))}
      <span className="sr-only">{t("common.loading")}</span>
    </div>
  );
}

/** Table-shaped skeleton: a header strip plus body rows. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="w-full overflow-hidden rounded-lg border" role="status" aria-busy="true">
      <div className="bg-muted/40 flex gap-4 border-b px-4 py-3">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} className="h-4 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 border-b px-4 py-4 last:border-b-0">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} className={cn("h-4 flex-1", colIndex === 0 && "max-w-48")} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Card-grid skeleton for dashboards. */
export function CardGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" role="status" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-28 w-full rounded-xl" />
      ))}
    </div>
  );
}

/**
 * Suspense fallback for the three lazy route roots.
 *
 * Deliberately chrome-shaped rather than a bare spinner: the sidebar rail and
 * topbar are drawn immediately so the shell does not appear to blink.
 */
export function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div className="bg-background flex min-h-svh" role="status" aria-busy="true">
      <div className="bg-sidebar hidden w-64 shrink-0 border-r p-4 lg:block">
        <Skeleton className="h-9 w-40 rounded-lg" />
        <div className="mt-6 space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-9 w-full rounded-md" />
          ))}
        </div>
      </div>
      <div className="flex-1">
        <div className="flex h-16 items-center gap-3 border-b px-4">
          <Skeleton className="h-9 w-9 rounded-md lg:hidden" />
          <Skeleton className="h-9 w-44 rounded-md" />
          <div className="flex-1" />
          <Skeleton className="size-9 rounded-full" />
        </div>
        <div className="space-y-4 p-4 sm:p-6">
          <Skeleton className="h-8 w-56 rounded-md" />
          <CardGridSkeleton />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
      <span className="sr-only">{t("common.loading")}</span>
    </div>
  );
}
